/// <amd-module name="Build/Run/Components/LogsView" />
import { IChecksPanelProps, IStageInfo, ReviewMode } from "Build/ApprovalsPanel/ChecksListPanel.props";
import { ApprovalUpdateParameters } from "Build/Client/Contracts/Approval";
import { TimelineRecordState } from "Build/Client/Contracts/Build";
import { CheckListItemType } from "Build/Client/Contracts/DataProvider";
import { ResourcePipelinePermissions } from "Build/Client/Contracts/PipelinePermissions";
import { CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import { IBuildLinkingService } from "Build/Client/Linking";
import { NavConstants } from "Build/Common/Library/Constants";
import { handleError } from "Build/Common/Library/ErrorHandler";
import { ChecksCard, IStageCheckData } from "Build/Run/Components/ChecksCard";
import { NoData } from "Build/Run/Components/NoData";
import { Telemetry } from "Build/Run/Constants";
import * as Resources from "Build/Run/Resources";
import { RunHeaderBreadcrumbService } from "Build/Run/RunHeaderBreadcrumbService";
import { IActionRecord, IBaseRecord, IRecord, IRunLogsData, IRunLogsService, IRunLogsTreeData, ITaskSection } from "Build/Run/RunLogsService.types";
import { ISignalRService } from "Build/Run/SignalRService";
import { LogViewer } from "PipelinesUI/LogViewer";
import { IFindResult } from "PipelinesUI/Utilities/LogParser.Types";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, RegionComponent, VssComponent, WrappedComponent, css } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Duration } from "VSSUI/Duration";
import { CustomHeader, HeaderBackButton, HeaderTitle, HeaderTitleArea } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { IListSelection, ListSelection } from "VSSUI/List";
import { SingleLayerMasterPanel, SingleLayerMasterPanelHeader } from "VSSUI/MasterDetails";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Status, StatusSize } from "VSSUI/Status";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITableColumn } from "VSSUI/Table";
import { TextField, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, Tree, renderTreeRow } from "VSSUI/TreeEx";
import { ScreenSizeConditional, ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import * as React from "react";

export interface ILogsViewProps extends IVssComponentProperties {}

const renderMainCell = (
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<IBaseRecord>,
    treeItem: ITreeItemEx<IBaseRecord>
): JSX.Element => {
    const record = treeItem.underlyingItem.data;
    const fullRecord = record as IRecord;
    const actionRecord = record as IActionRecord;
    const taskCompletion: JSX.Element | undefined =
        fullRecord.percentComplete && !fullRecord.end ? <span>{format(Resources.PercentComplete, fullRecord.percentComplete)}</span> : undefined;
    const duration: JSX.Element | undefined = fullRecord.start ? <Duration startDate={fullRecord.start} endDate={fullRecord.end} /> : undefined;
    const content: JSX.Element = (
        <div className={`job-detail text-ellipsis flex body-m flex-row flex-grow ${record.isJob || record.isTask ? "is-job" : ""}`}>
            <Tooltip text={record.name}>
                <div className="flex flex-center flex-grow text-ellipsis job-name-area">
                    {(record.isJob || record.isTask) && <Status {...record.statusProps} className="icon-large-margin" size={StatusSize.m} />}
                    <Link
                        className="text-ellipsis"
                        subtle={!actionRecord.onClick}
                        onClick={actionRecord.onClick}
                        removeUnderline={true}
                    >
                        <span>{record.name}</span>
                    </Link>
                </div>
            </Tooltip>
            <div className="job-duration flex flex-self-end">{taskCompletion || duration}</div>
        </div>
    );
    return ExpandableTreeCell({
        children: content,
        columnIndex,
        treeColumn,
        treeItem,
        className: `run-tree-cell ${record.isJob ? "run-job-cell" : ""}`
    });
};

const renderHeaderCell = (columnIndex: number, tableColumn: ITableColumn<ITreeItemEx<IBaseRecord>>): JSX.Element => {
    return (
        <th
            aria-colindex={columnIndex + 1}
            aria-readonly="true"
            className="body-m bolt-table-header-cell col-header-0"
            data-column-index="0"
            data-focuszone="false"
            tabIndex={-1}
            key={tableColumn.id}
        >
            <div className="bolt-table-header-cell-content flex-row">
                <div className="bolt-table-header-cell-text text-ellipsis body-m">{tableColumn.name}</div>
            </div>
        </th>
    );
};

const logsViewClass = "logs-view";

const darkThemeClassName = "dark-run-logs";

export class LogsView extends VssComponent<ILogsViewProps> {
    private _service: IRunLogsService;
    private _reader = React.createRef<LogViewer>();
    private _selection: IListSelection[];
    private _view: HTMLDivElement | null;
    private _signalR: ISignalRService;

    private _commandsUpdate: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _searchActive: boolean = false;
    private _searchText: ObservableValue<string> = new ObservableValue<string>("");
    private _search = React.createRef<TextField>();

    private _sections: IObservableArray<ITaskSection>;
    private _currentSelection: ObservableValue<IBaseRecord>;
    private _showTimestamps: ObservableValue<boolean> = new ObservableValue(false);
    private _globalMessageService: IGlobalMessagesService;
    private _projectId: string = "";
    private _showChecksPanel = new ObservableValue(false);
    private _checkListItemType: ObservableValue<CheckListItemType> = new ObservableValue(CheckListItemType.All);

    private _showMasterPanelXSmallScreen = new ObservableValue(true);

    private _activeElement?: HTMLElement;
    private _initialSelectionDone: boolean; // Set to true after a section (job, task etc) is selected in the initial render 

    constructor(props: ILogsViewProps, context: IVssComponentContext) {
        super(props, context);
        this._service = this.context.pageContext.getService<IRunLogsService>("IRunLogsService");
        const themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
        themeService.createThemedSelector("." + darkThemeClassName, t => (t.isDark ? t : themeService.getTheme("ms.vss-web.vsts-theme-dark")));
        this._selection = [];
        this._signalR = this.context.pageContext.getService<ISignalRService>("ISignalRService");
        this._service.logsData && this._onLogsDataChange(this._service.logsData.value);
        this._globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService"); 
        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }
    }

    public render() {
        if (!this._service.logsData || !this._service.rawData) {
            return <NoData />;
        }

        const useUpdatedChecksFlow = isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Checks.EnableChecksScalabilityPhase1Flow", false)
        const isChecksLogsDisabled = isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Checks.DisableUpdatedChecksLogs", false)
        const isAsyncCheckLogsFlowEnabled = isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Checks.EnableUpdatedChecksLogsAsync", false)

        return (
            <Surface background={SurfaceBackground.neutral}>
                <div className="flex flex-row run-logs-view" ref={x => (this._view = x)}>
                    <ScreenSizeConditional condition={screenSize => screenSize === ScreenSize.xsmall}>
                        <UncheckedObserver showOnSmallScreens={this._showMasterPanelXSmallScreen}>
                            <SingleLayerMasterPanel
                                renderContent={this._getLeftContent}
                                renderHeader={this._getLeftHeader}
                                className={this._showMasterPanelXSmallScreen ? "xs-master-panel-full" : ""}
                            />
                        </UncheckedObserver>
                    </ScreenSizeConditional>
                    <ScreenSizeConditional condition={screenSize => screenSize > ScreenSize.xsmall}>
                        <SingleLayerMasterPanel renderContent={this._getLeftContent} renderHeader={this._getLeftHeader} showOnSmallScreens={true} />
                    </ScreenSizeConditional>
                    <Page className={`${logsViewClass} right themed ${darkThemeClassName}`}>
                        <Observer data={{ observableValue: this._service.logsData, filter: this._onLogsDataChange }}>
                            {(props: { data: IRunLogsData }) => {
                                const data = props.data;
                                if (data) {
                                    const stageName = this._service.getCurrentStageName();
                                    return (
                                        <div className="flex flex-grow flex-column v-scroll-hidden" onKeyDown={this._onKeyDown}>
                                            <Observer element={this._currentSelection} y={this._commandsUpdate}>
                                                {(props: { element: IBaseRecord }) => {
                                                    return (
                                                        <CustomHeader className="log-header">
                                                            <ScreenSizeConditional condition={screenSize => screenSize === ScreenSize.xsmall}>
                                                                <HeaderBackButton
                                                                    buttonProps={{
                                                                        onClick: this._onLogsBackClick,
                                                                        text: format(Resources.JobsInRun, this._service.rawData.buildNumber)
                                                                    }}
                                                                />
                                                            </ScreenSizeConditional>
                                                            <div className="flex-row flex-grow flex-self-stretch">
                                                                <ScreenSizeConditional
                                                                    condition={screenSize => screenSize > ScreenSize.xsmall || !this._searchActive}
                                                                >
                                                                    <HeaderTitleArea>
                                                                        <HeaderTitle className="text-ellipsis">
                                                                            <div className="flex flex-row flex-center job-header">
                                                                                <div className="flex status">
                                                                                    <Status {...props.element.statusProps} size={StatusSize.l} />
                                                                                </div>
                                                                                <div className="text-ellipsis title-m">{props.element.name}</div>
                                                                            </div>
                                                                        </HeaderTitle>
                                                                    </HeaderTitleArea>
                                                                </ScreenSizeConditional>
                                                                {this._reader.current && (
                                                                    <Observer result={this._reader.current.getFindResult()}>
                                                                        {(props: { result: IFindResult | undefined }) => {
                                                                            const result = props.result;
                                                                            const noMatchFound =
                                                                                !!this._searchText.value && (!result || result.lines.length == 0);
                                                                            return (
                                                                                <ScreenSizeObserver>
                                                                                    {(screenProps: { screenSize: ScreenSize }) => {
                                                                                        const nonMobileScreen =
                                                                                            screenProps.screenSize > ScreenSize.xsmall;
                                                                                        if (!!this._searchText.value) {
                                                                                            announce(this._getFindResultText(result));
                                                                                        }
                                                                                        return (
                                                                                            <div
                                                                                                className={css(
                                                                                                    "find-area",
                                                                                                    nonMobileScreen
                                                                                                        ? "flex-row"
                                                                                                        : "flex-column flex-grow",
                                                                                                    this._searchActive && "active",
                                                                                                    noMatchFound && "error"
                                                                                                )}
                                                                                            >
                                                                                                <TextField
                                                                                                    ref={this._search}
                                                                                                    className="find-box"
                                                                                                    prefixIconProps={{ iconName: "Search" }}
                                                                                                    style={TextFieldStyle.inline}
                                                                                                    width={
                                                                                                        nonMobileScreen
                                                                                                            ? TextFieldWidth.standard
                                                                                                            : TextFieldWidth.auto
                                                                                                    }
                                                                                                    placeholder={Resources.SearchLabel}
                                                                                                    onChange={this._onSearch}
                                                                                                    value={this._searchText}
                                                                                                    onKeyDown={this._onSearchKeyDown}
                                                                                                />
                                                                                                {this._searchActive && (
                                                                                                    <span className="find-result body-s">
                                                                                                        {this._getFindResultText(result)}
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        );
                                                                                    }}
                                                                                </ScreenSizeObserver>
                                                                            );
                                                                        }}
                                                                    </Observer>
                                                                )}
                                                                <ScreenSizeObserver>
                                                                    {(screenProps: { screenSize: ScreenSize }) => {
                                                                        const smallScreen = screenProps.screenSize <= ScreenSize.xsmall;
                                                                        return (
                                                                            <HeaderCommandBar
                                                                                className="reader-actions flex-start"
                                                                                overflowClassName={`themed ${darkThemeClassName}`}
                                                                                items={this._getJobCommands(
                                                                                    props.element,
                                                                                    smallScreen && this._searchActive
                                                                                )}
                                                                            />
                                                                        );
                                                                    }}
                                                                </ScreenSizeObserver>
                                                            </div>
                                                        </CustomHeader>
                                                    );
                                                }}
                                            </Observer>
                                            <Observer showChecksPanel={this._showChecksPanel}>
                                                {(checksPanelObserverProps: { showChecksPanel: boolean }) => {
                                                    const stage = data.stage;
                                                    if (
                                                        stage &&
                                                        stage.checks &&
                                                        stage.checks.some(check => check.status === CheckRunStatus.Running)
                                                    ) {
                                                        const stages = [
                                                            {
                                                                id: stage.id,
                                                                name: stage.name,
                                                                checks: stage.checks
                                                            }
                                                        ] as IStageCheckData[];
                                                        const checksPanelProps: IChecksPanelProps = {
                                                            stages: stages as IStageInfo[],
                                                            runId: this._service.rawData.buildId,
                                                            pipelineId: this._service.rawData.definitionId,
                                                            checkListItemType: this._checkListItemType.value,
                                                            reviewMode: ReviewMode.Stage,
                                                            onSubmitBulkApproval: this._onSubmitBulkApproval,
                                                            onReassignApprovals: this._onReassignApprovals,
                                                            onPermitResource: this._onPermitResource,
                                                            onDismiss: this._onChecksPanelDismiss,
                                                            onRerunCheck: this._onRerunCheck,
                                                            onBypassCheck: this._onBypassCheck,
                                                        };

                                                        return (
                                                            checksPanelObserverProps.showChecksPanel && (
                                                                <div>
                                                                    <WrappedComponent
                                                                        dependencies={["ms.vss-build-web.approvals-panel"]}
                                                                        wrappedType={useUpdatedChecksFlow ? "ms.tfs-build.stage-checks-panel": "ms.tfs-build.approvals-list-panel"}
                                                                        {...checksPanelProps}
                                                                    />
                                                                </div>
                                                            )
                                                        );
                                                    } else {
                                                        return <div />;
                                                    }
                                                }}
                                            </Observer>
                                            {data.stage && (
                                                <ChecksCard
                                                    className="logs-checks-card body-m"
                                                    pageContext={this.context.pageContext}
                                                    projectId={this._projectId}
                                                    isChecksLogsDisabled={isChecksLogsDisabled}
                                                    isAsyncCheckLogsFlowEnabled={isAsyncCheckLogsFlowEnabled}
                                                    stages={[
                                                        {
                                                            id: data.stage.id,
                                                            name: stageName ? stageName : data.stage.name,
                                                            checks: data.stage.checks
                                                        }
                                                    ]}
                                                    onReviewClicked={this._onReviewClick}
                                                />
                                            )}
                                            {data.message && (
                                                <MessageCard
                                                    className="pipelines-run-checks-message attempt-message"
                                                    severity={MessageCardSeverity.Info}
                                                >
                                                    {data.message}
                                                </MessageCard>
                                            )}
                                            <UncheckedObserver showTimestamps={this._showTimestamps}>
                                                <LogViewer
                                                    ref={this._reader}
                                                    className="log-reader custom-scrollbar v-scroll-auto h-scroll-auto flex-grow"
                                                    sections={this._sections}
                                                />
                                            </UncheckedObserver>
                                            {this._sections != null && this._sections.value != null && this._sections.value[0] != null && (
                                                <RegionComponent
                                                    componentRegion="logs-view-insights"
                                                    childComponentProperties={{
                                                        pipelineId: this._service.rawData.definitionId,
                                                        pipelineRunId: this._service.rawData.buildId,
                                                        taskStatus: this._sections.value[0].statusProps,
                                                        taskName: this._sections.value[0].title,
                                                        jobId: this._service.getCurrentJobId(),
                                                        phaseId: this._service.getCurrentPhaseId(),
                                                        stageId: this._service.getCurrentStageId()
                                                    }}
                                                />
                                            )}
                                        </div>
                                    );
                                } else {
                                    return <div>...</div>;
                                }
                            }}
                        </Observer>
                    </Page>
                </div>
            </Surface>
        );
    }

    public componentDidMount() {
        const rawData = this._service.rawData;
        if (!rawData) {
            return;
        }

        this._service.selection.subscribe(this._onSelectionChanged);
        this._service.liveContent.subscribe(this._onLiveData);
        this._service.partialData.subscribe(this._onPartialData);
        this._onSelectionChanged();
        this._onLiveData();

        window.addEventListener("focus", this._onFocus);
        window.addEventListener("blur", this._onBlur);

        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const pipelineName = rawData.definitionName;
        const pipelineUrl = linkingService.getDefinitionUrl(rawData.definitionId);
        const runNumber = rawData.buildNumber;
        const runUrl = linkingService.getBuildUrl(rawData.buildId);

        const breadcrumbService = this.context.pageContext.getService<RunHeaderBreadcrumbService>("run-header-breadcrumb-service");
        breadcrumbService.setBreadcrumbs(pipelineName, pipelineUrl, runNumber, runUrl);

        // We should have everything on first render
        this.context.pageContext
            .getService<IVssPerformanceService>("IVssPerformanceService")
            .markTimeToInteractive(Telemetry.Area, Telemetry.RunLogs);

        this._signalR.watch(this._service.rawData.buildId);

        const scrollElement = document.querySelector("." + logsViewClass) as HTMLElement;
        if (scrollElement && this._reader.current) {
            this._reader.current.initializeScrollable(scrollElement);
        }

        document.title = format(Resources.TitleLogs, rawData.buildNumber);
        this._initialSelectionDone = true;
    }

    public componentWillUnmount() {
        this._service.selection.unsubscribe(this._onSelectionChanged);
        this._service.liveContent.unsubscribe(this._onLiveData);
        this._service.partialData.unsubscribe(this._onPartialData);

        window.removeEventListener("focus", this._onFocus);
        window.removeEventListener("blur", this._onBlur);

        const breadcrumbService = this.context.pageContext.getService<RunHeaderBreadcrumbService>("run-header-breadcrumb-service");
        breadcrumbService.clearBreadcrumbs();
        this._signalR.endWatch(this._service.rawData.buildId);
    }

    private _onReviewClick = (stages: IStageCheckData[]) => {
        this._showChecksPanel.value = true;
        this._checkListItemType.value = CheckListItemType.All;
        this._activeElement = document.activeElement as HTMLElement;
    };

    private _onChecksPanelDismiss = () => {
        this._showChecksPanel.value = false;
        if (this._activeElement) {
            this._activeElement.focus();
            this._activeElement = undefined;
        }
    };

    private _onSubmitBulkApproval = (approvalUpdates: ApprovalUpdateParameters[]) => {
        this._service
            .updateApprovals(approvalUpdates)
            .then(approvals => {
                if (approvals) {
                    this._service.refresh();
                    this._onChecksPanelDismiss();
                }
            })
            .catch(handleError);
    };

    private _onReassignApprovals = (approvalUpdates: ApprovalUpdateParameters[]): Promise<void> => {
        return this._service.updateApprovals(approvalUpdates).then(
            approvals => {
                if (approvals) {
                    this._service.refresh();
                    this._onChecksPanelDismiss();

                    // Display toast in reassign scenario
                    this._globalMessageService.addToast({
                        message: Resources.ApprovalReassigned,
                        duration: 5000,
                        forceOverrideExisting: true
                    });
                    return Promise.resolve();
                }
            },
            error => {
                return Promise.reject(error);
            }
        );
    };

    private _onRerunCheck = (checkId: string, checksuiteId: string): Promise<void> => {
        return this._service.rerunCheck(checkId, checksuiteId).then(
            checks => {
                if (checks) {
                    this._service.refresh();
                    this._onChecksPanelDismiss();
                    return Promise.resolve();
                }
            },
            error => {
                return Promise.reject(error);
            }
        );
    };

    private _onBypassCheck = (checkId: string, checksuiteId: string): Promise<void> => {
        return this._service.bypassCheck(checkId, checksuiteId).then(
            checks => {
                if (checks) {
                    this._service.refresh();
                    this._onChecksPanelDismiss();
                    return Promise.resolve();
                }
            },
            error => {
                return Promise.reject(error);
            }
        );
    };

    private _onPermitResource = (permissionsRequest: ResourcePipelinePermissions, resourceType: string, resourceId: string) => {
        this._service.updatePermissions(permissionsRequest, resourceType, resourceId).then(permissions => {
            if (permissions) {
                this._globalMessageService.addToast({
                    message: Resources.PermissionGranted,
                    duration: 5000,
                    forceOverrideExisting: true
                });
                this._service.refresh();
            }
        });
    };

    private _onDown = () => {
        if (this._reader.current) {
            this._reader.current.toEnd();
        }
    };

    private _onUp = () => {
        if (this._reader.current) {
            this._reader.current.toHome();
        }
    };

    private _onSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        if (this._reader.current) {
            this._searchText.value = value;
            this._reader.current.find(value);
        }
    };

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const key = event.key.toLowerCase();
        if ((event.metaKey || event.ctrlKey) && key === "f") {
            this._showFind();
            event.preventDefault();
        }
    };

    private _onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const key = event.key.toLowerCase();
        const reader = this._reader.current;
        if (key === "escape") {
            this._hideFind();
        } else if (event.shiftKey && key === "enter") {
            reader && reader.findPrev();
        } else if (key === "enter") {
            reader && reader.findNext();
        } else if (key == "tab") {
            requestAnimationFrame(() => {
                const upButton = document.getElementById("__bolt-log-up");
                upButton && upButton.focus();
            });
        }
    };

    private _getFindResultText(result: IFindResult | undefined): string {
        if (result) {
            return format(Resources.SearchMatch, result.toScrollIndex + 1, result.scrollCount);
        }

        return Resources.SearchNone;
    }

    private _getFarRight = (section: ITaskSection) => {
        const payload = section.payload;
        if (payload && payload.start) {
            return <Duration startDate={payload.start} endDate={payload.end} />;
        }

        return null;
    };

    private _getLeftHeader = () => {
        const rawData = this._service.rawData;
        return (
            <SingleLayerMasterPanelHeader
                title={format(Resources.JobsInRun, rawData.buildNumber)}
                subTitle={rawData.definitionName}
                onBackButtonClick={this._onBackClick}
            />
        );
    };

    private _getLeftContent = () => {
        let items: JSX.Element[] = [];

        this._service.treeData.value.items.forEach((stage, index) => {
            this._selection.push(new ListSelection(false));

            items.push(
                <Observer data={this._service.treeData} key={stage.data.id}>
                    {(props: { data: IRunLogsTreeData }) => {
                        const provider = new TreeItemProvider<IBaseRecord>(props.data.items[index].childItems || []);
                        const columns = [
                            {
                                hierarchical: true,
                                id: stage.data.id,
                                renderCell: renderMainCell,
                                name: stage.data.name,
                                renderHeaderCell: renderHeaderCell,
                                headerClassName: "body-m",
                                width: -100
                            } as ITreeColumn<IBaseRecord>
                        ];

                        return (
                            <Tree<IBaseRecord>
                                key={stage.data.id}
                                showHeader={true}
                                role="treegrid"
                                renderRow={(rowIndex, item, details) => {
                                    details.role = "row";
                                    const runSelection = this._service?.selection?.value;
                                    const itemData = item?.underlyingItem?.data;
                                    const selectedValueId = runSelection?.taskId ? runSelection?.taskId : runSelection?.jobId;
                                    if(!this._initialSelectionDone && selectedValueId === itemData?.id && !details.selection?.selected(rowIndex)){
                                        details.selection?.select(rowIndex);
                                    }

                                    return renderTreeRow(rowIndex, item, details, columns, itemData);
                                }}
                                columns={columns}
                                itemProvider={provider}
                                selection={this._selection[index]}
                                onToggle={(event, treeItem: ITreeItemEx<IBaseRecord>) => {
                                    const item = treeItem.underlyingItem;
                                    if (event.currentTarget.className.includes("expand-button") || !item.expanded) {
                                        provider.toggle(item);
                                    }
                                    if (item.data.isJob) {
                                        this._service.selectJob(item.data.id);
                                    } else if (item.data.isTask) {
                                        this._service.selectTask(item.data.id);
                                    }
                                }}
                                onSelect={(event, treeItem: ITreeRow<IBaseRecord>) => {
                                    const record = treeItem.data.underlyingItem.data;
                                    const selectedJob = this._service.selection.value && this._service.selection.value.jobId;
                                    // if we select an in progress task, go back to following tail
                                    // do not change to follow tail if we select the job associated with the tasks the user is currently viewing
                                    // do change to follow tail if we select another running job
                                    // do follow tail when a user selects a pending job so that when the job starts, we will follow its progress
                                    if (
                                        ((record as IRecord).state === TimelineRecordState.InProgress && selectedJob !== record.id) ||
                                        (record as IRecord).state === TimelineRecordState.Pending
                                    ) {
                                        this._service.setFollowTail(true);
                                    } else {
                                        this._service.setFollowTail(false);
                                    }

                                    if (record.isJob) {
                                        this._service.selectJob(record.id);
                                    } else if (record.isTask) {
                                        this._service.selectTask(record.id);
                                    }

                                    this._showMasterPanelXSmallScreen.value = false;
                                }}
                                scrollable={true}
                            />
                        );
                    }}
                </Observer>
            );
        });

        return <div className="run-logs-tree flex-grow flex-">{items}</div>;
    };

    private _onLogsDataChange = (data: IRunLogsData) => {
        this._normalize(data.sections);

        if (!this._sections) {
            this._sections = new ObservableArray<ITaskSection>(data.sections);
        } else {
            this._sections.value = data.sections;
        }

        if (!this._currentSelection) {
            this._currentSelection = new ObservableValue<IBaseRecord>(data.job);
        } else {
            this._currentSelection.value = data.job;
        }
        return true;
    };

    private _onPartialData = () => {
        const data = this._service.partialData.value;
        if (data) {
            if (data.updatedSections) {
                this._normalize(data.updatedSections);
                // we only show one section at a time in the logs view
                const newSection = data.updatedSections[0];
                const newSectionIndex = this._sections.value.findIndex(s => s.key === newSection.key);
                if (newSectionIndex > -1) {
                    // if we already have some data for this section, change it in place for the observable
                    this._sections.change(newSectionIndex, newSection);
                    this._sections.value.splice(newSectionIndex + 1, this._sections.value.length - newSectionIndex);
                    this._sections.value.splice(0, newSectionIndex);
                } else {
                    // so if the current section doesn't exist in the list of sections, just delete previous and replace
                    this._sections.value.splice(0, this._sections.length, ...data.updatedSections);
                }
            }
            if (data.currentJob) {
                this._currentSelection.value = data.currentJob;
            }
        }
    };

    private _normalize(sections: ITaskSection[]) {
        sections.forEach(x => {
            x.getFarRight = this._getFarRight;
            if (x.payload && x.payload.state === TimelineRecordState.Completed) {
                x.getLineLink = this._getLineLink;
            }
        });
    }

    private _getJobCommands(job: IBaseRecord, showImportantsOnly: boolean): IHeaderCommandBarItem[] {
        const items: IHeaderCommandBarItem[] = [];

        if (!showImportantsOnly) {
            if (!this._service.liveContent.value) {
                items.push({
                    id: "timestamp",
                    ariaLabel: Resources.ToggleTimestamps,
                    text: Resources.ToggleTimestamps,
                    important: false,
                    className: "subtle",
                    onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        this._toggleTimestamp();
                    }
                });
            }
        }

        if (!this._searchActive) {
            items.push({
                ariaLabel: Resources.SearchLabel,
                className: "subtle",
                id: "log-search",
                important: true,
                iconProps: { iconName: "Search" },
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this._showFind();
                },
                tooltipProps: {
                    text: Resources.SearchLabel
                }
            });
        } else {
            items.push({
                ariaLabel: Resources.SearchPrev,
                className: "subtle",
                id: "log-up",
                important: true,
                iconProps: { iconName: "ChevronUp" },
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this._reader.current && this._reader.current.findPrev();
                },
                tooltipProps: {
                    text: Resources.SearchPrev
                }
            });

            items.push({
                ariaLabel: Resources.SearchNext,
                className: "subtle",
                id: "down",
                important: true,
                iconProps: { iconName: "ChevronDown" },
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this._reader.current && this._reader.current.findNext();
                },
                tooltipProps: {
                    text: Resources.SearchNext
                }
            });

            items.push({
                ariaLabel: Resources.SearchClose,
                className: "subtle",
                id: "close",
                important: true,
                iconProps: { iconName: "ChromeClose" },
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this._hideFind();
                },
                tooltipProps: {
                    text: Resources.SearchClose
                }
            });
        }

        const jobRecord = job as IRecord;
        if (jobRecord.state === TimelineRecordState.Pending && this._service.canPriorityBeBumped(job.id)) {
            items.push({
                id: "priority",
                ariaLabel: Resources.RunNext,
                text: Resources.RunNext,
                important: true,
                className: "log-header-button",
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this._service.bumpJobPriority(job.id);
                    this._commandsUpdate.value = !this._commandsUpdate.value;
                },
                tooltipProps: {
                    text: Resources.RunNextTooltip
                }
            });
        }

        const downloadUrl = this._service.getLogUrl(job.id);
        if (downloadUrl) {
            items.push({
                id: "download",
                ariaLabel: Resources.AriaDownloadLog,
                text: Resources.AriaDownloadLog,
                important: true,
                className: "log-header-button",
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    if (event) {
                        // since this is a link, prevent default so that we don't open it in the current view itself
                        event.preventDefault();
                    }
                    // open raw log in new tab
                    const openedWindow = window.open(downloadUrl, "_blank");
                    if (openedWindow) {
                        openedWindow.opener = null;
                    }
                },
                href: downloadUrl
            });
        }

        return items;
    }

    private _showFind() {
        this._searchActive = true;
        this._commandsUpdate.value = !this._commandsUpdate.value;
        requestAnimationFrame(() => {
            this._search.current && this._search.current.focus();
        });
    }

    private _toggleTimestamp() {
        this._showTimestamps.value = !this._showTimestamps.value;
        // change observable to force render the content, we would need to update sections, not just remove and add new ones
        const map: { [key: string]: number } = {};
        const currentSections = this._sections.value.slice();
        currentSections.forEach((x, index) => {
            map[x.key] = index;
        });
        currentSections.forEach(x => {
            const indexToChange = map[x.key];
            this._sections.change(indexToChange, x);
        });
    }

    private _hideFind() {
        this._reader.current && this._reader.current.clearFind();
        this._searchActive = false;
        this._searchText.value = "";
        this._commandsUpdate.value = !this._commandsUpdate.value;
        requestAnimationFrame(() => {
            const logSearch = document.getElementById("__bolt-log-search");
            logSearch && logSearch.focus();
        });
    }

    private _onBackClick = () => {
        const pageContext = this.context.pageContext;
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const url = linkingService.getBuildUrl(this._service.rawData.buildId);
        onClickFPS(pageContext, url, true);
    };

    private _onLogsBackClick = () => {
        this._showMasterPanelXSmallScreen.value = true;
    };

    private _getLineLink = (section: ITaskSection, lineNumber: number) => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const routes: { [key: string]: string } = {};
        if (section.payload) {
            routes[NavConstants.job] = section.payload.jobId + "";
            if (section.payload.stageId) {
                routes[NavConstants.stage] = section.payload.stageId + "";
            }
        }

        routes[NavConstants.task] = section.key + "";
        routes[NavConstants.line] = lineNumber + "";
        const url = linkingService.getBuildUrl(
            (this._service.logsData as IObservableValue<IRunLogsData>).value.buildId,
            NavConstants.logs,
            undefined,
            undefined,
            routes
        );
        const fullUrl = linkingService.getAbsoluteUrl(url);
        return fullUrl;
    };

    private _onLiveData = () => {
        const liveData = this._service.liveContent.value;
        if (liveData && this._reader.current) {
            this._reader.current.updateContent(liveData.taskId);
        }
    };

    private _onSelectionChanged = () => {
        const runSelection = this._service.selection.value;
        if (runSelection) {
            if (runSelection.taskSelection) {
                if (this._reader.current) {
                    this._reader.current.select(runSelection.taskSelection.key, runSelection.taskSelection.lineToScroll);
                }
            }
        }
    };

     // User has switched back to the tab
    private _onFocus = (event: FocusEvent) => {
        this._service.setFocused(true);
    };

    // User has switched away from the tab (AKA tab is hidden)
    private _onBlur = (event: FocusEvent) => {
        this._service.setFocused(false)
    };
}

Components.add("run-logs-content", LogsView);
